import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, ChevronRight, Mail } from 'lucide-react';
import infoPages from '../data/infoPages';

/* ── Section renderer ── */
const Section = ({ s }) => {
  if (s.type === 'heading') return <h2 className="text-xl font-bold text-[#c30909] mt-8 mb-3">{s.text}</h2>;
  if (s.type === 'heading2') return <h3 className="text-base font-bold text-[#484c55] mt-6 mb-2">{s.text}</h3>;
  if (s.type === 'bold') return <p className="text-sm font-bold text-[#484c55] mt-4 mb-1">{s.text}</p>;
  if (s.type === 'text') return <p className="text-sm text-[#484c55]/80 leading-relaxed mb-3 whitespace-pre-line">{s.text}</p>;
  if (s.type === 'list') return (
    <ul className="space-y-1.5 mb-3 ml-4">
      {s.items.map((item, i) => (
        <li key={i} className="text-sm text-[#484c55]/80 leading-relaxed flex items-start gap-2">
          <span className="text-[#c30909] mt-0.5 flex-shrink-0">•</span>{item}
        </li>
      ))}
    </ul>
  );
  if (s.type === 'quote') return (
    <div className="bg-[#f0f0f1] rounded-xl p-6 my-6">
      <p className="text-[#c30909] text-4xl font-bold mb-2">"</p>
      <p className="text-base font-bold text-[#484c55] italic leading-relaxed">{s.text}</p>
      <p className="text-xs text-[#484c55]/50 mt-3">{s.author}</p>
    </div>
  );
  if (s.type === 'columns') return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
      {s.cols.map((col, i) => (
        <p key={i} className="text-sm text-[#484c55]/80 leading-relaxed whitespace-pre-line">{col}</p>
      ))}
    </div>
  );
  return null;
};

/* ── FAQ sub-item (expandable) ── */
const FaqItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 px-1 text-left hover:text-[#c30909] transition-colors"
      >
        <span className="text-sm font-medium">{item.title}</span>
        <ChevronRight size={16} className={`text-[#484c55]/40 transition-transform flex-shrink-0 ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="pb-4 px-1">
          {item.sections.map((s, i) => <Section key={i} s={s} />)}
        </div>
      )}
    </div>
  );
};

/* ── FAQ Category ── */
const FaqCategory = ({ cat }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-xl shadow-sm mb-3 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left"
      >
        <span className="text-base font-bold">{cat.title}</span>
        <ChevronRight size={18} className={`text-[#484c55]/40 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-2">
          {cat.items.map((item, i) => <FaqItem key={i} item={item} />)}
        </div>
      )}
    </div>
  );
};

/* ── Contact support block ── */
const ContactBlock = ({ data }) => (
  <div className="mt-8 bg-white rounded-xl p-6 shadow-sm">
    <h3 className="text-xl font-bold text-[#484c55] mb-2">{data.title}</h3>
    <p className="text-sm text-[#555] mb-4">{data.text}</p>
    <a href={`mailto:${data.email}`} className="inline-flex items-center gap-2 bg-[#c30909] hover:bg-[#a00707] text-white font-semibold text-xs uppercase tracking-wider px-6 py-3 rounded-lg transition-colors">
      <Mail size={14} />{data.btn}
    </a>
  </div>
);

/* ── Main Page ── */
const InfoPage = () => {
  const { slug } = useParams();
  const { t, lang } = useLanguage();
  const page = infoPages[slug];

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  if (!page) return (
    <div className="min-h-screen bg-[#f0f0f1] flex items-center justify-center">
      <p className="text-lg text-[#484c55]/50">404</p>
    </div>
  );

  const renderContent = () => {
    /* ── FAQ with categories ── */
    if (page.type === 'faq-categories') {
      const cats = page.categories[lang] || page.categories.en || page.categories.ru;
      const contact = page.contactBlock[lang] || page.contactBlock.en;
      return (
        <>
          {cats.map((cat, i) => <FaqCategory key={i} cat={cat} />)}
          <ContactBlock data={contact} />
        </>
      );
    }

    /* ── Rich text pages ── */
    if (page.type === 'rich-text') {
      const sections = page.content[lang] || page.content.en || page.content.ru;
      return (
        <div className="bg-white rounded-xl p-6 shadow-sm">
          {sections.map((s, i) => <Section key={i} s={s} />)}
        </div>
      );
    }

    /* ── Routes list ── */
    if (page.type === 'routes-list') {
      const routes = page.content[lang] || page.content.en || page.content.ru;
      return (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {routes.map((r, i) => (
            <div key={i} className={`flex items-center gap-4 px-5 py-4 ${i < routes.length - 1 ? 'border-b' : ''}`}>
              <div className="w-2 h-2 rounded-full bg-[#c30909] flex-shrink-0" />
              <div className="flex-1">
                <span className="text-sm font-bold text-[#484c55]">{r.route}</span>
              </div>
              <span className="text-xs text-[#484c55]/50">{r.schedule}</span>
              <span className="text-xs font-medium text-[#c30909]">{r.duration}</span>
            </div>
          ))}
        </div>
      );
    }

    /* ── Contacts ── */
    if (page.type === 'contacts') {
      const c = page.content[lang] || page.content.en;
      const labels = lang === 'ru'
        ? { phone: 'Телефон', hours: 'Часы работы', address: 'Адрес', web: 'Сайт' }
        : { phone: 'Phone', hours: 'Working hours', address: 'Address', web: 'Website' };
      return (
        <div className="bg-white rounded-xl p-6 shadow-sm space-y-5">
          <div>
            <p className="text-xs font-semibold text-[#484c55]/50 uppercase mb-1">{labels.phone}</p>
            <p className="text-xl font-bold text-[#c30909]">{c.phone}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-[#484c55]/50 uppercase mb-1">E-mail</p>
            <a href={`mailto:${c.email}`} className="text-sm font-medium text-[#c30909] hover:underline">{c.email}</a>
          </div>
          <div>
            <p className="text-xs font-semibold text-[#484c55]/50 uppercase mb-1">{labels.hours}</p>
            <p className="text-sm text-[#484c55]/70">{c.hours}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-[#484c55]/50 uppercase mb-1">{labels.address}</p>
            <p className="text-sm text-[#484c55]/70">{c.address}</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-[#484c55]/50 uppercase mb-1">{labels.web}</p>
            <p className="text-sm font-medium text-[#c30909]">{c.web}</p>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-[#f0f0f1]">
      <div className="max-w-[800px] mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#c30909] font-semibold mb-6 hover:underline">
          <ArrowLeft size={16} />
          {t('success.backHome')}
        </Link>
        <h1 className="text-2xl font-bold text-[#484c55] mb-6">{t(page.titleKey)}</h1>
        {renderContent()}
      </div>
    </div>
  );
};

export default InfoPage;
